import React from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "@/components/icons";
import { requestRegistrationSms, confirmRegistration, fetchAuthProviders, type PhoneRegistrationMode } from "@/lib/api";
import { driverPortalLoginHref, handOffToDriverPortal, safeDriverPortalReturn } from "@/lib/driverPortalHandoff";
import { serverErrors, useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

/**
 * Phone self-registration for platform drivers.
 *
 * Step 1: enter a phone → a code is texted. Step 2: enter the code + a password →
 * the account (and its driver profile) is created, any pending organization
 * invitations for that phone are consumed, and the driver is handed over to the
 * driver portal — to the safe `/charge/...` page in `?next=` (e.g. the charger they
 * came from), else the portal account page. When the account was created but the
 * browser could not be signed in, or joining the inviting organization failed, the
 * page stays put and says so, with an explicit way on. In `invite_only` mode an un-invited phone is told to ask a
 * manager; in `open` mode anyone may register.
 *
 * The whole flow sits behind the server's registration mode (off by default).
 * We ask /api/auth/providers up front so a deep link into /register (from an
 * invite SMS sent before the flag was flipped, or a bookmark) explains itself
 * instead of letting the user fill in a form that can only 404 on submit.
 */
type Outcome = { joinFailed: boolean; signedIn: boolean };

const REQUEST_SMS_ERRORS = { family: "registration_request_sms", fallback: "operator.auth.register_code_failed" } as const;

export function RegisterPage() {
  const t = useT();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [available, setAvailable] = React.useState<boolean | null>(null);
  const [mode, setMode] = React.useState<PhoneRegistrationMode | null>(null);
  const [step, setStep] = React.useState<1 | 2>(1);
  const [phone, setPhone] = React.useState("");
  const [code, setCode] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [username, setUsername] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [outcome, setOutcome] = React.useState<Outcome | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetchAuthProviders()
      // The field is optional on older builds; absent means "not offered".
      .then((p) => {
        if (cancelled) return;
        setAvailable(Boolean(p.phone_registration));
        setMode(p.phone_registration_mode ?? null);
      })
      // A failed probe must not lock people out of a working flow — fall back to
      // showing the form and let the endpoint itself be authoritative.
      .catch(() => !cancelled && setAvailable(true));
    return () => {
      cancelled = true;
    };
  }, []);

  async function requestCode(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await requestRegistrationSms(phone.trim());
      if (res.status === "sent") {
        toast.success(t("operator.auth.register_code_sent"));
        setStep(2);
      } else {
        // exists / not_invited / not_sent: the guidance for that status.
        toast.error(serverErrors.message(t, { status: 200, body: res }, REQUEST_SMS_ERRORS));
      }
    } catch (err) {
      toast.error(errorMessage(t, err, REQUEST_SMS_ERRORS));
    } finally {
      setLoading(false);
    }
  }

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    if (outcome) return; // the account exists; never submit it twice
    setLoading(true);
    try {
      const res = await confirmRegistration({
        phone: phone.trim(),
        code: code.trim(),
        password,
        username: username.trim() || undefined,
      });
      setCode("");
      setPassword("");
      const joinFailed = res.invitation_status === "failed";
      if (res.logged_in && !joinFailed) {
        toast.success(
          res.joined_organizations?.length
            ? t("operator.auth.register_created_joined")
            : t("operator.auth.register_created"),
        );
        handOffToDriverPortal(next);
        return;
      }
      // The account exists but something needs the person's attention — say so on the
      // page and let them continue explicitly, instead of navigating the message away.
      setOutcome({ joinFailed, signedIn: res.logged_in });
    } catch (err) {
      toast.error(errorMessage(t, err, { family: "registration_confirm", fallback: "operator.auth.register_failed" }));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-12 text-zinc-950 dark:bg-black dark:text-zinc-50">
      <div className="mx-auto w-full max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>{outcome ? t("operator.auth.register_created_title") : t("operator.auth.register_title")}</CardTitle>
            <CardDescription>
              {outcome
                ? t("operator.auth.register_ready")
                : mode === "open"
                ? t("operator.auth.register_description_open")
                : t("operator.auth.register_description_invite")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {outcome ? (
              <RegistrationOutcome outcome={outcome} next={next} />
            ) : available === null ? (
              <div className="py-6 text-center text-sm text-zinc-500">{t("common.state.loading")}</div>
            ) : !available ? (
              <div className="space-y-3 text-sm">
                <p>
                  {t("operator.auth.register_unavailable")}
                </p>
                <p className="text-zinc-500 dark:text-zinc-400">
                  {t("operator.auth.register_unavailable_hint")}
                </p>
              </div>
            ) : step === 1 ? (
              <form onSubmit={requestCode} className="space-y-4">
                <div>
                  <Label htmlFor="register-phone">{t("operator.auth.register_phone")}</Label>
                  <Input
                    id="register-phone"
                    className="mt-2"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t("operator.auth.register_phone_placeholder")}
                    autoFocus
                  />
                </div>
                <Button className="w-full" type="submit" disabled={loading || phone.trim().length < 5}>
                  {loading ? t("operator.auth.sending") : t("operator.auth.register_send_code")}
                </Button>
              </form>
            ) : (
              <form onSubmit={confirm} className="space-y-4">
                <div>
                  <Label htmlFor="register-code">{t("operator.auth.verification_code")}</Label>
                  <Input
                    id="register-code"
                    className="mt-2"
                    inputMode="numeric"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder={t("operator.auth.verification_code_placeholder")}
                    autoFocus
                  />
                </div>
                <div>
                  <Label htmlFor="register-username">{t("operator.auth.register_username")}</Label>
                  <Input
                    id="register-username"
                    className="mt-2"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t("operator.auth.register_username_placeholder")}
                  />
                </div>
                <div>
                  <Label htmlFor="register-password">{t("operator.auth.register_password", { min: 10 })}</Label>
                  <div className="relative mt-2">
                    <Input
                      id="register-password"
                      className="pr-10"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-label={showPassword ? t("operator.auth.hide_password") : t("operator.auth.show_password")}
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <Button className="w-full" type="submit" disabled={loading || code.trim().length < 4 || password.length < 10}>
                  {loading ? t("operator.auth.register_creating") : t("operator.auth.register_create")}
                </Button>
                <button
                  type="button"
                  className="w-full text-center text-xs text-zinc-500 underline hover:text-zinc-700 dark:text-zinc-400"
                  onClick={() => setStep(1)}
                >
                  {t("operator.auth.register_change_phone")}
                </button>
              </form>
            )}

            {!outcome && (
              <div className="mt-4 text-center">
                {/* Drivers sign in on the portal (another SPA), returning where they came from. */}
                <a
                  href={driverPortalLoginHref(next)}
                  className="text-xs text-zinc-500 underline hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                >
                  {t("operator.auth.register_have_account")}
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** The account exists; what still needs the person, and the one way on. */
function RegistrationOutcome({ outcome, next }: { outcome: Outcome; next: string | null }) {
  const t = useT();
  return (
    <div className="space-y-4 text-sm">
      {outcome.joinFailed && (
        <div
          role="alert"
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
        >
          <p className="font-medium">{t("operator.auth.register_join_failed_title")}</p>
          <p className="mt-1">
            {t("operator.auth.register_join_failed_body")}
          </p>
        </div>
      )}
      {outcome.signedIn ? (
        <Button asChild className="w-full">
          <a href={safeDriverPortalReturn(next)}>{t("operator.auth.register_continue")}</a>
        </Button>
      ) : (
        <>
          <p role="status">
            {t("operator.auth.register_not_signed_in")}
          </p>
          <Button asChild className="w-full">
            <a href={driverPortalLoginHref(next)}>{t("operator.auth.sign_in")}</a>
          </Button>
        </>
      )}
    </div>
  );
}
